import { useState, useContext, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { UserContext } from 'contexts/user';
import * as EmailValidator from 'email-validator';

import { ReactComponent as ArrowRightSVG } from 'icons/arrow-right.svg';

import ViewComponent from 'pages/intellio/view_component';
import Button from 'components/button';
import IntellioWarning from 'components/intelio_warning/account';

import { createAccount } from 'api/invoices';

const IntellioAccountCreate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const { user } = useContext(UserContext);
  const [localUser, setLocalUser] = useState(user);
  const history = useHistory();

  const [validation, setValidation] = useState({
    firstName: true,
    lastName: true,
    email: true,
    rpps: true,
    specialty: true,
    finesId: true,
  });

  const [validationText, setValidationText] = useState({
    firstName: 'This question is required!',
    lastName: 'This question is required!',
    email: 'This question is required!',
    specialty: 'This question is required!',
    rpps: 'This question is required!',
    finesId: 'This question is required!',
  });

  const checkValidity = useCallback(() => {
    const { email, rpps, finesId } = localUser;
    const isEmailValid = EmailValidator.validate(email);
    const isRppsValid = rpps?.length === 11 && !isNaN(rpps);
    const isFinesIdValid =
      finesId?.length === 9 && finesId?.match(/^[a-zA-Z0-9]+$/);

    const validation = {
      firstName: !!localUser.firstName.trim(),
      lastName: !!localUser.lastName.trim(),
      email: isEmailValid,
      specialty: !!localUser.specialty,
      rpps: isRppsValid,
      finesId: isFinesIdValid,
    };

    const newValidationText = { ...validationText };

    if (!isEmailValid && !!email) {
      newValidationText.email = 'Please enter a valid email';
    } else if (email?.length === 0) {
      newValidationText.email = 'This question is required!';
    }

    if (!isRppsValid && !!rpps) {
      newValidationText.rpps = 'RPPS number must contain 11 digits';
    } else if (rpps?.length === 0) {
      newValidationText.rpps = 'This question is required!';
    }

    if (!isFinesIdValid && !!finesId) {
      newValidationText.finesId = 'FINESS number must contain 9 characters';
    } else if (finesId?.length === 0) {
      newValidationText.finesId = 'This question is required!';
    }

    setValidation(validation);
    setValidationText(newValidationText);

    return Object.values(validation).every(Boolean);
  }, [localUser, validationText]);

  const onSubmit = useCallback(async () => {
    setLoading(true);

    if (checkValidity()) {
      const res = await createAccount({
        id: localUser.id,
        rpps_identifier: localUser.rpps,
        fines_id: localUser.finesId,
        first_name: localUser.firstName,
        last_name: localUser.lastName,
        email: localUser.email,
        specialty_code: localUser.specialty,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setError();
        history.push('/profile');

        window.open(
          `${process.env.REACT_APP_INTELLIO_URL}${res.url || res.detail}`,
          '_blank',
        );
      }
    }

    setLoading(false);
  }, [checkValidity, localUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue =
      name === 'firstName' || name === 'lastName' ? value : value.trim();

    setLocalUser({
      ...localUser,
      [name]: trimmedValue,
    });
  };

  const onSpecialtyChange = (selectedOption) => {
    setLocalUser({
      ...localUser,
      specialty: selectedOption ? selectedOption.value : null,
    });
  };

  return (
    <main className="th-main">
      <section className="th-canvas">
        <header className="th-canvas-header post-header">
          <h2 className="th-canvas-title" data-test-screen-title>
            <span className="pointer" onClick={() => history.goBack()}>
              My profile
            </span>
            <span>
              <ArrowRightSVG />
            </span>
            <span>Intellio Next account creation</span>
          </h2>
          <section className="view-actions">
            <Button
              className="btn-green"
              label="Create"
              color="green"
              loading={loading}
              onClick={onSubmit}
            />
          </section>
        </header>
        {error && (
          <IntellioWarning
            title="There was an error creating your Intellio Next account!"
            message={error}
          />
        )}
        <section className="view-container">
          <ViewComponent
            user={localUser}
            onChange={handleChange}
            onSpecialtyChange={onSpecialtyChange}
            validation={validation}
            validationText={validationText}
          />
        </section>
      </section>
    </main>
  );
};
export default IntellioAccountCreate;
